import { useState, useCallback, useMemo } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { DateInput } from '../../../components/inputs/DatePicker'
import { Checkbox } from '../../../components/inputs/Checkbox'
import { Button } from '../../../components/buttons'
import { intl } from '../../../../i18n'
import { ApiTokenScope } from '../../../../domain/entity/ApiTokenScope'
import { colorPalette } from '../../../style/colorPalette'

const fieldLabelStyle = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: colorPalette.monotone[5],
  marginBottom: 1,
}

const DateInputWrapper = styled(Box)(({ theme }) => ({
  width: '160px',
  height: '34.25px',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  border: '1px solid',
  borderColor:
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.23)'
      : 'rgba(255, 255, 255, 0.23)',
  borderRadius: '4px',
  padding: '0 14px',
  '&:hover': {
    borderColor: theme.palette.text.primary,
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
    padding: '0 13px',
  },
}))

type Props = {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    serviceName: string
    expiredAt: number
    scopes: string[]
  }) => void
  loading: boolean
  availableScopes: ApiTokenScope[]
}

const CreateTokenDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  availableScopes,
}: Props) => {
  const today = useMemo(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  }, [])

  const defaultExpiredAt = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() + 1)
    return date
  }, [])

  const [serviceName, setServiceName] = useState('')
  const [expiredAt, setExpiredAt] = useState<Date | undefined>(defaultExpiredAt)
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])

  const onScopeChange = useCallback((scopeName: string, checked: boolean) => {
    setSelectedScopes(prev =>
      checked ? [...prev, scopeName] : prev.filter(s => s !== scopeName)
    )
  }, [])

  const onSubmitClick = useCallback(() => {
    const trimmedServiceName = serviceName.trim()
    if (!trimmedServiceName || !expiredAt || selectedScopes.length === 0) {
      return
    }
    const endOfDay = new Date(expiredAt)
    endOfDay.setHours(23, 59, 59, 999)
    onSubmit({
      serviceName: trimmedServiceName,
      expiredAt: endOfDay.getTime(),
      scopes: selectedScopes,
    })
  }, [serviceName, expiredAt, selectedScopes, onSubmit])

  const onCloseDialog = useCallback(() => {
    setServiceName('')
    setExpiredAt(defaultExpiredAt)
    setSelectedScopes([])
    onClose()
  }, [onClose, defaultExpiredAt])

  const isValid =
    serviceName.trim().length > 0 &&
    expiredAt &&
    expiredAt >= today &&
    selectedScopes.length > 0

  return (
    <Dialog open={open} onClose={onCloseDialog} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        {intl.formatMessage({
          id: 'apiTokenManagement.createServiceDialog.title',
        })}
      </DialogTitle>
      <DialogContent sx={{ paddingTop: '16px !important' }}>
        <Box sx={{ marginBottom: 3 }}>
          <Typography sx={fieldLabelStyle}>
            {intl.formatMessage({ id: 'apiTokenManagement.serviceName' })}
          </Typography>
          <TextField
            autoFocus={true}
            size="small"
            fullWidth={true}
            variant="outlined"
            value={serviceName}
            onChange={({ target: { value } }) => setServiceName(value)}
            disabled={loading}
          />
        </Box>
        <Box sx={{ marginBottom: 3 }}>
          <Typography sx={fieldLabelStyle}>
            {intl.formatMessage({ id: 'apiTokenManagement.expiredAt' })}
          </Typography>
          <DateInputWrapper>
            <DateInput
              value={expiredAt}
              onChange={setExpiredAt}
              editable={!loading}
              placeholder="YYYY/MM/DD"
              popperPlacement="bottom-start"
              minDate={today}
            />
          </DateInputWrapper>
        </Box>
        <Box>
          <Typography sx={fieldLabelStyle}>
            {intl.formatMessage({ id: 'apiTokenManagement.scopes' })}
          </Typography>
          <FormGroup sx={{ paddingLeft: 1 }}>
            {availableScopes.map(({ scopeName, displayName }) => (
              <FormControlLabel
                key={scopeName}
                control={
                  <Checkbox
                    size="s"
                    checked={selectedScopes.includes(scopeName)}
                    onChange={({ target: { checked } }) =>
                      onScopeChange(scopeName, checked)
                    }
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2">
                    {`${scopeName} - ${displayName}`}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button
          colorPattern="monotone"
          variant="outlined"
          onClick={onCloseDialog}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        <Button
          variant="filled"
          colorPattern="skyBlue"
          onClick={onSubmitClick}
          disabled={!isValid || loading}
        >
          {intl.formatMessage({
            id: 'apiTokenManagement.createServiceTokenButton',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateTokenDialog
